import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { IconCourseCourseClose2 } from '@doltech/icons/Course/IconCourseCourseClose2';
import { IconSystemV2CourseCheck2 } from '@doltech/icons/SystemV2/IconSystemV2CourseCheck2';
import { IconSystemV2CourseInfoFill } from '@doltech/icons/SystemV2/IconSystemV2CourseInfoFill';
import { IconSystemV2EssentialEye } from '@doltech/icons/SystemV2/IconSystemV2EssentialEye';
import { IconSystemV2EssentialEyeCossed } from '@doltech/icons/SystemV2/IconSystemV2EssentialEyeCossed';
import { ButtonGeneral } from '@doltech/ui/lib/figma/Button/ButtonGeneral/ButtonGeneral';
import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import * as React from 'react';
import styled from 'styled-components';
import { KeyboardInstructionItem } from '../KeyboardInstructionItem/KeyboardInstructionItem';
import { DictationSentenceStatusEnum, DictationWordTypeEnum } from '@doltech/utils/lib/constants';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';

const ContentWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  gap: 6px;
`;

const IconWrapper = styled.div`
  height: 20px;
  display: flex;
  align-items: center;
`;

const Instruction = styled.div`
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  gap: 8px;

  .mac-text {
    display: none;
  }

  ${fromScreen(1024)} {
    .mac-text {
      display: initial;
    }
  }
`;

const TextIcon = styled.div`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 8px;
`;

const Main = styled.div`
  padding: 0px 20px 20px;

  display: grid;
  align-items: center;
  gap: 20px;

  ${fromScreen(600)} {
    grid-template-columns: minmax(0, 1fr) max-content;
    gap: 12px;
  }
`;

const ActionWrapper = styled.div`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 12px;

  ${fromScreen(600)} {
    grid-template-columns: repeat(2, minmax(0, max-content));
  }

  &.three-btn {
    grid-template-columns: repeat(3, minmax(0, max-content));
  }
`;

const Suggest = styled.div``;
const Check = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 2px solid ${colorsV2.grey20};
  border-radius: 12px;
  cursor: pointer;
`;

interface FooterFreeTextTypeProps {
  isEnd?: boolean;
  isRedoDisabled?: boolean;
  isCheckDisabled?: boolean;
  status?: DictationSentenceStatusEnum;
  words: {
    key?: number;
    type?: string;
    value?: string;
  }[];
  highlightText?: any;
  wordsChecked?: any;
  onCheck?: () => void;
  onRedo?: () => void;
  onNext?: () => void;
  onReOrder?: () => void;
  onSubmit?: () => void;
}

export const FooterFreeTextType = (props: FooterFreeTextTypeProps) => {
  const {
    status = DictationSentenceStatusEnum.UNTOUCHED,
    isRedoDisabled,
    isCheckDisabled,
    isEnd,
    words,
    highlightText,
    wordsChecked,
    onCheck,
    onRedo,
    onNext,
    onSubmit,
  } = props;
  const [suggestTextVisibility, setSuggestTextVisibility] = React.useState(false);

  const renderInProgressText = React.useCallback(
    (word, idx) => {
      if (word?.type === DictationWordTypeEnum.PUNCTUATION) {
        return (
          <ResponsiveTypography.Text key={idx} variant="Regular/20-28" color="grey180">
            {word?.value}{' '}
          </ResponsiveTypography.Text>
        );
      }

      const punctuationCount = words.filter(
        (x, wIdx) => x?.type === DictationWordTypeEnum.PUNCTUATION && wIdx <= idx
      ).length;

      const correctAnswerIndex =
        (highlightText?.filter((x) => x?.className === 'correct')?.length || 0) + punctuationCount;

      if (word?.isWordCorrect && idx <= correctAnswerIndex) {
        return (
          <ResponsiveTypography.Text key={idx} variant="Regular/20-28" color="grey180">
            {word?.value}{' '}
          </ResponsiveTypography.Text>
        );
      }

      if (correctAnswerIndex + 1 === idx + 1) {
        return (
          <ResponsiveTypography.Text key={idx} variant="Regular/20-28" color="green100">
            {word?.value}{' '}
          </ResponsiveTypography.Text>
        );
      }

      return suggestTextVisibility ? (
        <ResponsiveTypography.Text key={idx} variant="Regular/20-28" color="grey180">
          {word?.value}{' '}
        </ResponsiveTypography.Text>
      ) : (
        <ResponsiveTypography.Text key={idx} variant="Regular/20-28" color="grey180">
          {new Array(word?.value?.length).fill('*').join('')}{' '}
        </ResponsiveTypography.Text>
      );
    },
    [highlightText, words, suggestTextVisibility]
  );

  switch (status) {
    case DictationSentenceStatusEnum.CORRECT:
      return (
        <Main className="student-footer-quiz">
          <TextIcon>
            <IconWrapper>
              <IconSystemV2CourseCheck2 color="green100" size={16} />
            </IconWrapper>
            <Instruction>
              <ResponsiveTypography.Paragraph variant="semi-bold/14-20" color="green100">
                Chính xác! Bạn làm tốt lắm!
              </ResponsiveTypography.Paragraph>
              <ContentWrapper>
                <ResponsiveTypography.Paragraph variant="medium/14-20" color="black60">
                  Nhấn phím
                </ResponsiveTypography.Paragraph>
                <KeyboardInstructionItem text="Enter" />
                <ResponsiveTypography.Paragraph variant="medium/14-20" color="black60">
                  để qua câu.
                </ResponsiveTypography.Paragraph>
              </ContentWrapper>
            </Instruction>
          </TextIcon>
          {isEnd ? (
            <ButtonGeneral type="primary" size="large" text="Kết thúc bài" onClick={onSubmit} />
          ) : (
            <ButtonGeneral type="primary" size="large" text="Tiếp theo" onClick={onNext} />
          )}
        </Main>
      );

    case DictationSentenceStatusEnum.IN_PROGRESS:
      return (
        <Main className="student-footer-quiz">
          <TextIcon>
            <IconWrapper>
              <IconCourseCourseClose2 color="red100" size={16} />
            </IconWrapper>
            <Suggest>
              <ResponsiveTypography.Paragraph variant="semi-bold/14-20" color="red100" mb={8}>
                Chưa đúng! Bạn xem gợi ý để làm lại nhé!
              </ResponsiveTypography.Paragraph>
              {wordsChecked?.map((word, idx) => renderInProgressText(word, idx))}
            </Suggest>
          </TextIcon>
          <ActionWrapper className="three-btn">
            <Check onClick={() => setSuggestTextVisibility(!suggestTextVisibility)}>
              {suggestTextVisibility ? (
                <IconSystemV2EssentialEyeCossed color="grey180" size={24} />
              ) : (
                <IconSystemV2EssentialEye color="grey180" size={24} />
              )}
            </Check>
            <ButtonGeneral
              type="secondary"
              size="large"
              text="Làm lại"
              onClick={onRedo}
              disabled={isRedoDisabled}
            />
            <ButtonGeneral
              type="primary"
              size="large"
              text="Kiểm tra"
              onClick={onCheck}
              disabled={isCheckDisabled}
            />
          </ActionWrapper>
        </Main>
      );

    default:
      return (
        <Main className="student-footer-quiz">
          <div>
            <TextIcon>
              <IconWrapper>
                <IconSystemV2CourseInfoFill color="blue100" size={16} />
              </IconWrapper>
              <Instruction>
                <ContentWrapper>
                  <ResponsiveTypography.Paragraph variant="medium/14-20" color="black60">
                    Nhấn phím
                  </ResponsiveTypography.Paragraph>
                  <KeyboardInstructionItem text="Enter" />
                  <ResponsiveTypography.Paragraph variant="medium/14-20" color="black60">
                    để kiểm tra.
                  </ResponsiveTypography.Paragraph>
                </ContentWrapper>
                <ResponsiveTypography.Paragraph
                  variant="medium/14-20"
                  color="black60"
                  className="mac-text"
                >
                  Chuyển qua <b>Unikey Eng</b> để tránh lỗi typing trên Macbook.
                </ResponsiveTypography.Paragraph>
              </Instruction>
            </TextIcon>
          </div>
          <ActionWrapper className="two-btn">
            <ButtonGeneral
              type="secondary"
              size="large"
              text="Làm lại"
              onClick={onRedo}
              disabled={isRedoDisabled}
            />
            <ButtonGeneral
              type="primary"
              size="large"
              text="Kiểm tra"
              onClick={onCheck}
              disabled={isCheckDisabled}
            />
          </ActionWrapper>
        </Main>
      );
  }
};
